import { AREAS, MILESTONES, SHOP, type Area, type ClientAction } from "@squire/core";
import { ANIMATIONS, type AwayPlace } from "@squire/sprites";

/** Debug mode (Settings): chat commands starting with "/" drive the avatar and the game state. */
export type DebugCommand =
  | { kind: "reply"; text: string }
  | { kind: "anim"; id: string; ms: number; text: string }
  | { kind: "go"; place: AwayPlace | "out" | "home"; ms: number; friend: boolean; text: string }
  | { kind: "act"; action: ClientAction; text: string }
  | { kind: "reset"; text: string };

const PLACES: Record<string, AwayPlace | "out" | "home"> = {
  kawiarnia: "cafe",
  cafe: "cafe",
  bar: "bar",
  park: "park",
  biuro: "office",
  office: "office",
  wyjscie: "out",
  wyjście: "out",
  out: "out",
  dom: "home",
  home: "home",
};

const AREA_ALIASES: Record<string, Area> = {
  cialo: "body",
  ciało: "body",
  body: "body",
  glowa: "mind",
  głowa: "mind",
  mind: "mind",
  praca: "work",
  work: "work",
  relacje: "social",
  social: "social",
  dom: "home",
  home: "home",
};

const HELP = [
  "/anim <id> [sekundy] — animacja (lista: /anim)",
  "/idz kawiarnia|bar|park|biuro|wyjscie|dom [minuty]",
  "/balagan 0–3 — bałagan w mieszkaniu",
  "/xp cialo|glowa|praca|relacje|dom <ile> — dodaj XP (meble, poziomy)",
  "/zloto <ile> — dodaj złoto",
  "/forma cialo|glowa|relacje <0–100> [dni temu] — ustaw formę (np. /forma cialo 100 40 = 40 dni przerwy)",
  "/przedmiot <id> — daj mebel (lista: /przedmiot)",
  "/tryb normal|sick|vacation|crunch",
  "/reset TAK — wszystko od zera (poziomy, dziennik, questy; ustawienia zostają)",
].join("\n");

const num = (v: string | undefined, def: number) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : def;
};

/** Parse a debug command; null when the text is not a command. */
export function parseDebug(raw: string): DebugCommand | null {
  const text = raw.trim();
  if (!text.startsWith("/")) return null;
  const [cmd = "", ...args] = text.slice(1).split(/\s+/);
  const a0 = args[0]?.toLowerCase();
  switch (cmd.toLowerCase()) {
    case "pomoc":
    case "help":
      return { kind: "reply", text: HELP };
    case "anim": {
      if (!a0 || !ANIMATIONS[a0])
        return { kind: "reply", text: `Animacje: ${Object.keys(ANIMATIONS).join(", ")}` };
      const s = num(args[1], 15);
      return { kind: "anim", id: a0, ms: s * 1000, text: `Animacja ${a0} przez ${s} s.` };
    }
    case "idz":
    case "idź":
    case "go": {
      const place = a0 ? PLACES[a0] : undefined;
      if (!place) return { kind: "reply", text: `Gdzie? ${Object.keys(PLACES).join(", ")}` };
      const min = num(args[1], place === "office" ? 10 : 1);
      const friend = place === "cafe" || place === "bar" || place === "park";
      return {
        kind: "go",
        place,
        ms: min * 60_000,
        friend,
        text: place === "home" ? "Wracam do domu." : `Idę: ${a0} na ${min} min.`,
      };
    }
    case "balagan":
    case "bałagan":
    case "mess": {
      const level = Math.min(3, Math.max(0, Math.round(Number(a0 ?? 0)) || 0));
      return { kind: "act", action: { type: "debug", op: "mess", level }, text: `Bałagan: ${level}.` };
    }
    case "xp": {
      const area = a0 ? AREA_ALIASES[a0] : undefined;
      if (!area) return { kind: "reply", text: `Obszar: ${AREAS.join(", ")} (albo po polsku)` };
      const amount = num(args[1], 100);
      return {
        kind: "act",
        action: { type: "debug", op: "xp", area, amount },
        text: `+${amount} XP (${area}).`,
      };
    }
    case "forma":
    case "form": {
      const area = a0 ? AREA_ALIASES[a0] : undefined;
      if (area !== "body" && area !== "mind" && area !== "social")
        return { kind: "reply", text: "Forma: cialo, glowa albo relacje" };
      const pct = Math.min(100, Math.max(0, Math.round(Number(args[1] ?? 100)) || 0));
      const ago = Math.max(0, Math.round(Number(args[2] ?? 0)) || 0);
      return {
        kind: "act",
        action: { type: "debug", op: "form", area, amount: pct, level: ago },
        text: `Forma ${a0}: ${pct}%${ago ? `, ostatnio ${ago} dni temu` : ""}.`,
      };
    }
    case "zloto":
    case "złoto":
    case "gold": {
      const amount = num(a0, 100);
      return { kind: "act", action: { type: "debug", op: "gold", amount }, text: `+${amount} złota.` };
    }
    case "przedmiot":
    case "item": {
      const ids = [...MILESTONES.map((m) => m.id), ...SHOP.map((s) => s.id)];
      if (!a0 || !ids.includes(a0)) return { kind: "reply", text: `Przedmioty: ${ids.join(", ")}` };
      return { kind: "act", action: { type: "debug", op: "item", itemId: a0 }, text: `Daję: ${a0}.` };
    }
    case "tryb":
    case "mode": {
      const modes = ["normal", "sick", "vacation", "crunch"] as const;
      const mode = modes.find((m) => m === a0);
      if (!mode) return { kind: "reply", text: `Tryby: ${modes.join(", ")}` };
      return { kind: "act", action: { type: "set_mode", mode }, text: `Tryb: ${mode}.` };
    }
    case "reset":
      return args[0] === "TAK"
        ? { kind: "reset", text: "Kasuję postęp i zaczynamy od nowa." }
        : { kind: "reply", text: "To skasuje cały postęp. Na pewno? Napisz: /reset TAK" };
    default:
      return { kind: "reply", text: `Nie znam /${cmd}.\n${HELP}` };
  }
}
